import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";

// API's
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";

// Components
import MultipleSelectGroup from "@Layouts/components/MultipleSelectGroup/MultipleSelectGroup";

// MUI
import { Grid } from "@mui/material";
import { ISystemSettings } from "@/Settings/types/Common/model";
import { errorLogger } from "@/Common/helpers/logger";
import InfoSubTitle from "../../../common/InfoSubTitle";
import { getPositionsByDepartments } from "@/Common/helpers/Positions";

type ToPositionsProps = {
	locationId: string;
	toPositions?: string[];
	mappedPositions?: { systemPosition: string }[];
	isPayFundedDealsOnly: boolean;
};

export default function ToPositions(props: Readonly<ToPositionsProps>) {
	const { locationId, mappedPositions, toPositions, isPayFundedDealsOnly } = props;
	const queryClient = useQueryClient();
	const [selectedPositions, setSelectedPositions] = useState<string[]>([""]);
	const allowedPositions = getPositionsByDepartments(["Sales", "Finance"]);

	const uniquePositions = [...new Set(mappedPositions?.map((item: { systemPosition: string }) => item.systemPosition) ?? [])];
	const selectableOptions = uniquePositions
		.filter((item) => allowedPositions.some((entry) => item.startsWith(entry)))
		.map((item) => ({ label: item }))
		.filter((item) => !selectedPositions.includes(item.label));

	const { mutate, isPending } = useMutation({
		mutationFn: (input: { citCommissionPayout: { isPayFundedDealsOnly: boolean; toPositions: string[] } }) =>
			updateSystemSettings(locationId, input, "CIT_COMMISSION_PAYOUT"),
		onSuccess: (data) => {
			queryClient.setQueryData(["systemSettings", locationId], (oldData: ISystemSettings) => ({
				...oldData,
				citCommissionPayout: { ...oldData.citCommissionPayout, toPositions: data.citCommissionPayout.toPositions },
			}));
		},
		onError: (error) => errorLogger(error),
	});

	const handleAdd = () => setSelectedPositions([...selectedPositions, ""]);

	const handleChange = (selected: { label: string }, order: number) => {
		const updatedItems = [...selectedPositions];
		updatedItems[order] = selected.label;
		setSelectedPositions(updatedItems);
		const update = {
			citCommissionPayout: {
				isPayFundedDealsOnly: !!isPayFundedDealsOnly,
				toPositions: updatedItems,
			},
		};
		mutate(update);
	};

	const handleClear = (order: number) => {
		const updatedItems = [...selectedPositions];
		updatedItems[order] = "";
		setSelectedPositions(updatedItems);
	};

	const handleDelete = (order: number) => {
		const updatedItems = [...selectedPositions];
		updatedItems.splice(order, 1);
		const update = {
			citCommissionPayout: {
				isPayFundedDealsOnly: !!isPayFundedDealsOnly,
				toPositions: updatedItems,
			},
		};
		const oldData = { citCommissionPayout: { toPositions, isPayFundedDealsOnly } };
		if (!isEqual(oldData, update)) {
			mutate(update);
		}
		setSelectedPositions(updatedItems);
	};

	useEffect(() => {
		setSelectedPositions(toPositions || [""]);
		return () => {
			setSelectedPositions([""]);
		};
	}, [toPositions, locationId]);

	return (
		<Grid container>
			<Grid item container direction={"row"}>
				<Grid item xs={12}>
					<InfoSubTitle className={"regular-montserrat-label !mb-[12px]"} title="To Positions:" />
					<Grid item className="position-mapping-payroll-column" sx={{ position: "relative" }}>
						<MultipleSelectGroup
							items={selectedPositions}
							selectableOptions={selectableOptions}
							placeholder="Select an Option"
							showBy="label"
							selectBy="label"
							orderBy="label"
							deleting={isPending}
							onChange={(selected, order) => {
								if (selected) {
									handleChange(selected, order);
								} else {
									handleClear(order);
								}
							}}
							onDelete={handleDelete}
							onAdd={handleAdd}
							disabled={isPending}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}
